import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft,
  Save,
  Play,
  Pause,
  Settings,
  History,
  BarChart3,
  ChevronRight,
  Plus,
  Mail,
  MessageSquare,
  Clock,
  GitBranch,
  Zap,
  CheckSquare,
  Tag,
  User,
  FileText,
  DollarSign,
  Bell,
  Webhook,
  X,
  Search,
  GripVertical,
  Trash2,
  MoreVertical,
  AlertCircle,
  Eye,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  RefreshCw,
  Users,
  XCircle,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import {
  ActionEditorUnsavedProvider,
  useActionEditorUnsaved,
} from '../../../context/ActionEditorUnsavedContext';
import WorkflowActionsPanel from '../../../components/automations/WorkflowActionsPanel';
import WorkflowTriggerPicker from '../../../components/automations/WorkflowTriggerPicker';
import EmailNodeEditor from '../../../components/automations/EmailNodeEditor';
import EmailActionComposer from '../../../components/automations/EmailActionComposer';
import WaitNodeEditor from '../../../components/automations/WaitNodeEditor';
import SMSNodeEditor from '../../../components/automations/SMSNodeEditor';
import WebhookNodeEditor from '../../../components/automations/WebhookNodeEditor';
// Import real workflow action editors
import {
  GrantPortalAccessEditor,
  RevokePortalAccessEditor,
  GrantOfferAccessEditor,
  RevokeOfferAccessEditor,
  ApplyTagEditor,
  RemoveTagEditor,
  InternalNotificationEditor,
  WaitEditor,
} from '../../../components/automations/WorkflowActionEditors';
import IfElseConditionEditor, { createDefaultIfElseConfig } from '../../../components/automations/IfElseConditionEditor';
import WorkflowConditionBranchCanvas from '../../../components/automations/WorkflowConditionBranchCanvas';
import { BRANCH_CARD_MIN_WIDTH, isConditionStep, shouldRenderMainChainEndNode } from '../../../utils/workflowConditionUtils';
import WorkflowBuilderStepList from '../../../components/automations/WorkflowBuilderStepList';
import WorkflowBuilderMainChain from '../../../components/automations/WorkflowBuilderMainChain';
import WorkflowStepCard from '../../../components/automations/WorkflowStepCard';
import WorkflowTriggerCanvasSection from '../../../components/automations/WorkflowTriggerCanvasSection';
import {
  buildTriggerSaveFields,
  createTriggerFromSelection,
  hydrateWorkflowTriggersFromServer,
  normalizeWorkflowTriggers,
  syncLegacyTriggerFields,
} from '../../../utils/workflowTriggerUtils';
import { isSmsStep } from '../../../utils/workflowSmsUtils';
import WorkflowBuilderUtilityShell from '../../../components/automations/WorkflowBuilderUtilityShell';
import useWorkflowEditorLayout from '../../../hooks/useWorkflowEditorLayout';
import WorkflowBuilderChrome from '../../../components/automations/WorkflowBuilderChrome';
import WorkflowRunTestPanel from '../../../components/automations/WorkflowRunTestPanel';
import { toast } from 'sonner';
import {
  downloadAgencyWorkflow,
  getSafeWorkflowPortableError,
  triggerBrowserDownload,
} from '../../../services/workflowPortableService';
import { getAgencyBuilderPath } from '../../../services/workflowListService';
import WorkflowImportDialog from '../../../components/automations/WorkflowImportDialog';
import {
  createWorkflowImportOpeners,
  createWorkflowImportedHandler,
} from '../../../utils/workflowBuilderPortability';
import PreparePasswordResetEditor from '../../../components/automations/PreparePasswordResetEditor';
import { useWorkflowBuilderCanvasControls } from '../../../hooks/useWorkflowBuilderCanvasControls';
import {
  applyGroupConfigToSteps,
  buildDisplaySteps,
  createStepsFromCatalogAction,
  GROUP_TYPES,
} from '../../../utils/workflowStepGrouping';
import WorkflowSettingsTab from '../../../components/automations/WorkflowSettingsTab';
import WorkflowEnrollmentHistoryTab from '../../../components/automations/WorkflowEnrollmentHistoryTab';
import WorkflowExecutionLogsTab from '../../../components/automations/WorkflowExecutionLogsTab';
import WorkflowStatisticsPanel from '../../../components/automations/WorkflowStatisticsPanel';
import { DEFAULT_WORKFLOW_SETTINGS, normalizeWorkflowSettings } from '../../../utils/defaultWorkflowSettings';
import { getWorkflowStepDescription } from '../../../utils/workflowBuilderUtils';
import { fetchExecutionPath } from '../../../utils/workflowInsightsApi';
import { WORKFLOW_MAIN_COLUMN_WIDTH } from '../../../utils/workflowCardAnchors';
import { getWorkflowActionLaneLayout, isSameTriggerConnectorGeometry } from '../../../utils/workflowTriggerConnectorLayout';
import {
  createBranchAddTarget,
  createMainAddTarget,
  duplicateBranchStepAfter,
  findBranchStepLocation,
  insertCatalogActionAtTarget,
  removeBranchStepFromCondition,
  updateBranchStepInCondition,
} from '../../../utils/workflowConditionBranchSteps';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mock template data for when loaded from template
const TEMPLATE_DATA = {
  'tpl_001': {
    name: 'New Client Welcome Sequence',
    trigger: { id: 'client_created', type: 'client_created', label: 'Client Created' },
    steps: [
      { id: 'step_1', type: 'wait', config: { duration: 1, unit: 'hours', configured: true } },
      { id: 'step_2', type: 'send_email', config: { template: 'welcome_email', subject: 'Welcome to {{agency_name}}!', configured: true } },
      { id: 'step_3', type: 'wait', config: { duration: 2, unit: 'days', configured: true } },
      { id: 'step_4', type: 'send_email', config: { template: 'document_request', subject: 'Documents Needed for Your Credit Repair', configured: true } },
      { id: 'step_5', type: 'create_task', config: { title: 'Follow up with new client', assignee: 'owner', configured: true } },
    ],
  },
  'tpl_003': {
    name: 'Missing Document Reminder',
    trigger: { id: 'document_requested', type: 'document_requested', label: 'Document Requested' },
    steps: [
      { id: 'step_1', type: 'wait', config: { duration: 2, unit: 'days', configured: true } },
      { id: 'step_2', type: 'send_email', config: { template: 'doc_reminder_1', subject: 'Reminder: Documents Still Needed', configured: true } },
      { id: 'step_3', type: 'wait', config: { duration: 3, unit: 'days', configured: true } },
      { id: 'step_4', type: 'send_sms', config: { message: 'Hi {{first_name}}, we still need your documents to proceed. Please upload them at your convenience.', configured: true } },
    ],
  },
};

// Step type configurations - Complete action type dictionary
const STEP_TYPES = {
  send_email: { label: 'Send Email', icon: Mail, color: 'blue', requiresChannel: 'email' },
  send_sms: { label: 'Send SMS', icon: MessageSquare, color: 'green', requiresChannel: 'sms' },
  wait: { label: 'Wait', icon: Clock, color: 'amber', requiresChannel: null },
  wait_until: { label: 'Wait Until Date', icon: Clock, color: 'amber', requiresChannel: null },
  condition: { label: 'If/Else', icon: GitBranch, color: 'purple', requiresChannel: null },
  if_else: { label: 'If/Else', icon: GitBranch, color: 'purple', requiresChannel: null },
  create_task: { label: 'Create Task', icon: CheckSquare, color: 'teal', requiresChannel: null },
  // Tag actions
  apply_tag: { label: 'Apply Tag', icon: Tag, color: 'pink', requiresChannel: null },
  add_tag: { label: 'Add Tag', icon: Tag, color: 'pink', requiresChannel: null },
  remove_tag: { label: 'Remove Tag', icon: Tag, color: 'slate', requiresChannel: null },
  // Contact/Status actions
  update_contact: { label: 'Update Contact', icon: User, color: 'indigo', requiresChannel: null },
  update_status: { label: 'Update Status', icon: Zap, color: 'orange', requiresChannel: null },
  assign_user: { label: 'Assign User', icon: User, color: 'indigo', requiresChannel: null },
  // Opportunity actions
  create_opportunity: { label: 'Create Opportunity', icon: DollarSign, color: 'emerald', requiresChannel: null },
  move_opportunity_stage: { label: 'Move Stage', icon: Zap, color: 'teal', requiresChannel: null },
  // Notification/Communication
  send_notification: { label: 'Internal Notification', icon: Bell, color: 'cyan', requiresChannel: null },
  internal_notification: { label: 'Internal Notification', icon: Bell, color: 'cyan', requiresChannel: null },
  webhook: { label: 'Webhook', icon: Webhook, color: 'gray', requiresChannel: null },
  send_doc_reminder: { label: 'Document Reminder', icon: FileText, color: 'orange', requiresChannel: 'email' },
  send_billing_reminder: { label: 'Billing Reminder', icon: DollarSign, color: 'emerald', requiresChannel: 'email' },
  // Workflow actions
  start_workflow: { label: 'Start Workflow', icon: Zap, color: 'emerald', requiresChannel: null },
  stop_workflow: { label: 'Stop Workflow', icon: XCircle, color: 'red', requiresChannel: null },
  // Portal access actions
  grant_portal_access: { label: 'Grant Portal Access', icon: Zap, color: 'emerald', requiresChannel: null },
  revoke_portal_access: { label: 'Revoke Portal Access', icon: XCircle, color: 'red', requiresChannel: null },
  lock_self_repair_access: { label: 'Lock Self Repair', icon: XCircle, color: 'red', requiresChannel: null },
  unlock_self_repair_access: { label: 'Unlock Self Repair', icon: Zap, color: 'emerald', requiresChannel: null },
  send_portal_invite: { label: 'Send Portal Invite', icon: Mail, color: 'blue', requiresChannel: 'email' },
};

// Step Node Component
const StepNode = ({
  step,
  onRemove,
  onEdit,
  channelStatus,
  validationIssues,
  isMoving,
  isSelected,
  onDragHandleStart,
  onContextMenuAction,
  hasClipboard,
  readOnly = false,
  highlighted = false,
  pathState = null,
  columnWidth,
  isBranchStep = false,
}) => {
  const stepConfig = STEP_TYPES[step.type] || { label: 'Unknown', icon: Zap, color: 'slate' };
  const stepIssue = validationIssues?.find((i) => i.step_id === step.id);
  const hasChannelIssue = stepIssue?.blocking;
  const displayLabel = step.label || step.name || stepConfig.label;

  return (
    <WorkflowStepCard
      step={step}
      stepConfig={stepConfig}
      title={displayLabel}
      typeLabel={stepConfig.label}
      description={getWorkflowStepDescription(step, stepConfig)}
      requiresBackend={Boolean(step.config?.requires_backend)}
      hasChannelIssue={hasChannelIssue}
      channelIssueMessage={stepIssue?.message}
      isMoving={isMoving}
      isSelected={isSelected}
      highlighted={highlighted}
      pathState={pathState}
      readOnly={readOnly}
      hasClipboard={hasClipboard}
      onEdit={onEdit}
      onRemove={onRemove}
      onDragHandleStart={onDragHandleStart}
      onContextMenuAction={onContextMenuAction}
      columnWidth={columnWidth}
      isBranchStep={isBranchStep}
    />
  );
};

const WorkflowBuilderPageInner = () => {
  const { guardAction } = useActionEditorUnsaved();
  const { agencyId, workflowId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  const templateId = searchParams.get('template');
  const isNewWorkflow = workflowId === 'new';

  // Workflow state
  const [workflow, setWorkflow] = useState({
    name: 'Untitled Workflow',
    status: 'draft',
    trigger: null,
    triggers: [],
    steps: [],
    canvas_notes: [],
    comments: [],
    settings: { ...DEFAULT_WORKFLOW_SETTINGS },
  });
  const [loading, setLoading] = useState(!isNewWorkflow);
  const [saving, setSaving] = useState(false);
  const [isDownloadingWorkflow, setIsDownloadingWorkflow] = useState(false);
  const [workflowImportOpen, setWorkflowImportOpen] = useState(false);
  const [workflowImportMode, setWorkflowImportMode] = useState('file');
  const [activeTab, setActiveTab] = useState('builder');
  const [executionPathView, setExecutionPathView] = useState(null);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [showTriggerPicker, setShowTriggerPicker] = useState(false);
  const [triggerPickerMode, setTriggerPickerMode] = useState('add');
  const [editingTriggerId, setEditingTriggerId] = useState(null);
  const [pendingAddTarget, setPendingAddTarget] = useState(null);
  const [editingBranchContext, setEditingBranchContext] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [showRunTestPanel, setShowRunTestPanel] = useState(false);
  const [runTestExpanded, setRunTestExpanded] = useState(false);
  const nameEditSnapshot = useRef(null);
  
  // Node editor state
  const [editingStep, setEditingStep] = useState(null);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [showWaitEditor, setShowWaitEditor] = useState(false);
  const [showSMSEditor, setShowSMSEditor] = useState(false);
  const [showWebhookEditor, setShowWebhookEditor] = useState(false);
  const [showGrantPortalEditor, setShowGrantPortalEditor] = useState(false);
  const [showRevokePortalEditor, setShowRevokePortalEditor] = useState(false);
  const [showGrantOfferEditor, setShowGrantOfferEditor] = useState(false);
  const [showRevokeOfferEditor, setShowRevokeOfferEditor] = useState(false);
  const [showApplyTagEditor, setShowApplyTagEditor] = useState(false);
  const [showRemoveTagEditor, setShowRemoveTagEditor] = useState(false);
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const [showNotificationEditor, setShowNotificationEditor] = useState(false);
  const [showPreparePasswordResetEditor, setShowPreparePasswordResetEditor] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [triggerConnectorLayout, setTriggerConnectorLayout] = useState(null);
  const triggerConnectorLayoutRef = useRef(null);
  const pendingAddTargetRef = useRef(null);

  const handleTriggerConnectorLayoutChange = useCallback((layout) => {
    if (isSameTriggerConnectorGeometry(triggerConnectorLayoutRef.current, layout)) {
      return;
    }
    triggerConnectorLayoutRef.current = layout;
    setTriggerConnectorLayout(layout);
  }, []);

  const actionLaneLayout = useMemo(
    () => getWorkflowActionLaneLayout(triggerConnectorLayout),
    [triggerConnectorLayout],
  );

  const closeAllPanels = () => {
    setShowActionsPanel(false);
    setShowTriggerPicker(false);
    setShowRunTestPanel(false);
    setRunTestExpanded(false);
    pendingAddTargetRef.current = null;
    setPendingAddTarget(null);
    setEditingBranchContext(null);
    setShowEmailEditor(false);
    setShowWaitEditor(false);
    setShowSMSEditor(false);
    setShowWebhookEditor(false);
    setShowGrantPortalEditor(false);
    setShowRevokePortalEditor(false);
    setShowGrantOfferEditor(false);
    setShowRevokeOfferEditor(false);
    setShowApplyTagEditor(false);
    setShowRemoveTagEditor(false);
    setShowConditionEditor(false);
    setShowNotificationEditor(false);
    setShowPreparePasswordResetEditor(false);
    setEditingStep(null);
    setEditingGroup(null);
  };

  const closeEditorOverlay = closeAllPanels;
  
  // Channel status and validation
  const [channelStatus, setChannelStatus] = useState(null);
  const [validationIssues, setValidationIssues] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [publishError, setPublishError] = useState(null);

  // Fetch workflow
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (isNewWorkflow || !token || !agencyId || !workflowId) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/workflows/${agencyId}/${workflowId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to load workflow');
        const wf = await response.json();
        // Hydrate triggers from the richest available source so an empty
        // triggers array from the server does not wipe a legacy trigger.
        const hydratedTriggers = hydrateWorkflowTriggersFromServer(wf);
        // TEMP DEBUG (trigger persistence investigation) - remove later
        console.debug(
          '[trigger-persistence] hydrated workflow',
          workflowId,
          hydratedTriggers.map((t) => t.trigger_type || t.id),
        );
        setWorkflow(syncLegacyTriggerFields({
          name: wf.name || 'Untitled Workflow',
          status: wf.status || 'draft',
          steps: wf.steps || [],
          canvas_notes: wf.canvas_notes || [],
          comments: wf.comments || [],
          settings: normalizeWorkflowSettings(wf.settings),
        }, hydratedTriggers));
      } catch (error) {
        console.error('Failed to fetch workflow:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkflow();
  }, [agencyId, workflowId, isNewWorkflow, token]);

  const { undoRedo, recordWorkflowChange } = useWorkflowBuilderCanvasControls({
    workflow,
    setWorkflow,
    setHasUnsavedChanges,
    enabled: activeTab === 'builder',
  });

  useEffect(() => {
    if (!loading) {
      undoRedo.clearHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, loading]);

  // Fetch channel status
  const fetchChannelStatus = useCallback(async () => {
    if (!token || !agencyId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/workflows/${agencyId}/channel-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChannelStatus(data.channels);
      }
    } catch (error) {
      console.error('Failed to fetch channel status:', error);
    }
  }, [token, agencyId]);

  const handleSettingsChange = (patch) => {
    recordWorkflowChange('Updated workflow settings');
    setWorkflow((prev) => ({
      ...prev,
      settings: { ...normalizeWorkflowSettings(prev.settings), ...patch },
    }));
    setHasUnsavedChanges(true);
  };

  const handleViewExecutionPath = async (row) => {
    const executionId = row.execution_id || row.id;
    if (!executionId || !token || isNewWorkflow) return;
    try {
      const path = await fetchExecutionPath({
        token,
        workflowId,
        executionId,
        agencyId,
      });
      setExecutionPathView({
        ...path,
        contact_id: row.contact_id || path.contact_id,
        contact_name: row.contact_name,
        enrollment_date: row.date_enrolled || row.enrollment_date,
        workflow_version: row.workflow_version,
      });
      setActiveTab('builder');
    } catch (error) {
      console.error('Failed to load execution path:', error);
    }
  };

  // Load template data if creating from template
  useEffect(() => {
    if (templateId && TEMPLATE_DATA[templateId]) {
      const template = TEMPLATE_DATA[templateId];
      setWorkflow(syncLegacyTriggerFields({
        name: template.name + ' (Draft)',
        status: 'draft',
        trigger: template.trigger,
        triggers: template.trigger ? [template.trigger] : [],
        steps: template.steps,
      }));
      setHasUnsavedChanges(true);
    }
  }, [templateId]);

  // Fetch channel status on mount
  useEffect(() => {
    fetchChannelStatus();
  }, [fetchChannelStatus]);

  const handleAddStep = (index) => {
    closeAllPanels();
    pendingAddTargetRef.current = createMainAddTarget(index);
    setPendingAddTarget(pendingAddTargetRef.current);
    setShowActionsPanel(true);
  };

  const handleAddBranchStep = (conditionStepId, branchId, insertIndex, parentPath = []) => {
    closeAllPanels();
    pendingAddTargetRef.current = createBranchAddTarget({
      conditionStepId,
      branchId,
      insertIndex,
      parentPath,
    });
    setPendingAddTarget(pendingAddTargetRef.current);
    setShowActionsPanel(true);
  };

  const handleSelectAction = (action) => {
    guardAction(() => {
      recordWorkflowChange('Added step');
      const result = insertCatalogActionAtTarget(workflow.steps, pendingAddTargetRef.current, action);
      setWorkflow((prev) => ({ ...prev, steps: result.newSteps }));
      setShowActionsPanel(false);
      pendingAddTargetRef.current = null;
      setPendingAddTarget(null);
      setHasUnsavedChanges(true);

      if (result.isGroup) {
        const groupItem = buildDisplaySteps(result.insertedSteps, { advancedMode: false }).display[0];
        if (groupItem?.type === 'group') {
          openEditGroupInternal(groupItem);
        }
        return;
      }

      setEditingBranchContext(result.insertedTarget?.type === 'branch' ? {
        conditionStepId: result.insertedTarget.conditionStepId,
        branchId: result.insertedTarget.branchId,
      } : null);
      openStepEditorInternal(result.primaryStep);
    });
  };

  const handleSelectTrigger = (trigger) => {
    recordWorkflowChange('Updated trigger');
    setWorkflow((prev) => {
      const currentTriggers = normalizeWorkflowTriggers(prev);
      let nextTriggers;
      if (triggerPickerMode === 'edit' && editingTriggerId) {
        nextTriggers = currentTriggers.map((item) => (
          item.id === editingTriggerId
            ? createTriggerFromSelection({
              ...trigger,
              name: trigger.name || trigger.label,
              label: trigger.label || trigger.name,
            }, { preserveId: editingTriggerId })
            : item
        ));
      } else {
        nextTriggers = [...currentTriggers, createTriggerFromSelection({
          ...trigger,
          name: trigger.name || trigger.label,
          label: trigger.label || trigger.name,
        })];
      }
      return syncLegacyTriggerFields(prev, nextTriggers);
    });
    setHasUnsavedChanges(true);
    setEditingTriggerId(null);
    setTriggerPickerMode('add');
  };

  const handleTriggersChange = (nextTriggers) => {
    recordWorkflowChange('Updated triggers');
    setWorkflow((prev) => syncLegacyTriggerFields(prev, nextTriggers));
    setHasUnsavedChanges(true);
    if (editingTriggerId && !nextTriggers.some((item) => item.id === editingTriggerId)) {
      setShowTriggerPicker(false);
      setEditingTriggerId(null);
      setTriggerPickerMode('add');
    }
  };

  const openAddTriggerPicker = () => {
    closeAllPanels();
    setTriggerPickerMode('add');
    setEditingTriggerId(null);
    setShowTriggerPicker(true);
  };

  const openEditTriggerPicker = (trigger) => {
    closeAllPanels();
    setTriggerPickerMode('edit');
    setEditingTriggerId(trigger.id);
    setShowTriggerPicker(true);
  };

  const openEditGroupInternal = (groupItem) => {
    closeAllPanels();
    setEditingGroup(groupItem);
    if (groupItem.groupType === GROUP_TYPES.PREPARE_PASSWORD_RESET) {
      setShowPreparePasswordResetEditor(true);
    }
  };

  const openEditGroup = (groupItem) => {
    guardAction(() => openEditGroupInternal(groupItem));
  };

  const handleSaveGroup = (nextConfig) => {
    if (!editingGroup) return;
    recordWorkflowChange('Updated grouped step settings');
    setWorkflow((prev) => ({
      ...prev,
      steps: applyGroupConfigToSteps(prev.steps, editingGroup, nextConfig),
    }));
    setHasUnsavedChanges(true);
  };

  const openStepEditorInternal = (step, branchContext = null) => {
    closeAllPanels();
    setEditingStep(step);
    setEditingBranchContext(branchContext);
    
    switch (step.type) {
      case 'send_email':
      case 'send_doc_reminder':
      case 'send_billing_reminder':
        setShowEmailEditor(true);
        break;
      case 'wait':
        setShowWaitEditor(true);
        break;
      case 'send_sms':
      case 'sms':
      case 'sms_action':
        setShowSMSEditor(true);
        break;
      case 'webhook':
        setShowWebhookEditor(true);
        break;
      case 'grant_portal_access':
        setShowGrantPortalEditor(true);
        break;
      case 'revoke_portal_access':
        setShowRevokePortalEditor(true);
        break;
      case 'grant_offer_access':
        setShowGrantOfferEditor(true);
        break;
      case 'revoke_offer_access':
        setShowRevokeOfferEditor(true);
        break;
      case 'apply_tag':
      case 'add_tag':
        setShowApplyTagEditor(true);
        break;
      case 'remove_tag':
        setShowRemoveTagEditor(true);
        break;
      case 'condition':
      case 'if_else':
        setShowConditionEditor(true);
        break;
      case 'internal_notification':
      case 'send_notification':
        setShowNotificationEditor(true);
        break;
      default:
        if (isSmsStep(step)) {
          setShowSMSEditor(true);
          break;
        }
        console.log(`No editor for step type: ${step.type}`);
        break;
    }
  };

  const openStepEditor = (step) => {
    if (step?.id && step.id === editingStep?.id) return;
    const branchLocation = findBranchStepLocation(workflow.steps, step?.id);
    guardAction(() => openStepEditorInternal(
      step,
      branchLocation
        ? { conditionStepId: branchLocation.conditionStepId, branchId: branchLocation.branchId }
        : null,
    ));
  };

  const handleSaveStep = (updatedStep) => {
    recordWorkflowChange('Updated step settings');
    setWorkflow((prev) => ({
      ...prev,
      steps: editingBranchContext
        ? updateBranchStepInCondition(prev.steps, editingBranchContext, updatedStep)
        : prev.steps.map((s) => (s.id === updatedStep.id ? updatedStep : s)),
    }));
    setHasUnsavedChanges(true);
  };

  const handleRemoveStep = (stepId) => {
    recordWorkflowChange('Deleted step');
    const branchLocation = findBranchStepLocation(workflow.steps, stepId);
    if (branchLocation) {
      setWorkflow((prev) => ({
        ...prev,
        steps: removeBranchStepFromCondition(prev.steps, branchLocation, stepId),
      }));
      if (editingStep?.id === stepId) {
        setEditingStep(null);
        setEditingBranchContext(null);
        setShowEmailEditor(false);
        setShowWaitEditor(false);
        setShowSMSEditor(false);
        setShowWebhookEditor(false);
      }
      setHasUnsavedChanges(true);
      return;
    }

    const grouped = buildDisplaySteps(workflow.steps, { advancedMode: false }).display
      .find((item) => item.type === 'group' && item.stepIds.includes(stepId));
    setWorkflow((prev) => ({
      ...prev,
      steps: grouped
        ? prev.steps.filter((s) => !grouped.stepIds.includes(s.id))
        : prev.steps.filter((s) => s.id !== stepId),
    }));
    if (editingStep?.id === stepId) {
      setEditingStep(null);
      setEditingBranchContext(null);
      setShowEmailEditor(false);
      setShowWaitEditor(false);
      setShowSMSEditor(false);
      setShowWebhookEditor(false);
    }
    setHasUnsavedChanges(true);
  };

  const handleBranchStepContextAction = (action, stepId, branchContext) => {
    if (action === 'delete') {
      handleRemoveStep(stepId);
      return;
    }
    if (action === 'copy' && branchContext) {
      const { steps: nextSteps } = duplicateBranchStepAfter(
        workflow.steps,
        branchContext,
        stepId,
      );
      setWorkflow((prev) => ({ ...prev, steps: nextSteps }));
      setHasUnsavedChanges(true);
    }
  };

  const handleStepsChange = (nextSteps) => {
    recordWorkflowChange('Changed workflow steps');
    setWorkflow((prev) => ({
      ...prev,
      steps: nextSteps,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!token || !agencyId) return;
    // Never save while the initial GET is still loading; an empty draft state
    // must not overwrite the persisted trigger.
    if (loading) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      const triggerFields = buildTriggerSaveFields(workflow);
      // TEMP DEBUG (trigger persistence investigation) - remove later
      console.debug(
        '[trigger-persistence] saving workflow',
        workflowId,
        triggerFields.trigger?.key ?? null,
      );
      const payload = {
        name: workflow.name,
        ...triggerFields,
        steps: workflow.steps,
        canvas_notes: workflow.canvas_notes || [],
        comments: workflow.comments || [],
        settings: workflow.settings,
        status: workflow.status,
      };

      if (isNewWorkflow) {
        const response = await fetch(`${API_URL}/api/workflows/${agencyId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Failed to create workflow');
        const created = await response.json();
        setHasUnsavedChanges(false);
        navigate(`/agency/${agencyId}/automations/builder/${created.workflow_id}`);
      } else {
        const response = await fetch(`${API_URL}/api/workflows/${agencyId}/${workflowId}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Failed to save workflow');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to save workflow:', error);
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = useCallback(async () => {
    if (isNewWorkflow || !workflowId || !agencyId || isDownloadingWorkflow) {
      if (isNewWorkflow || !workflowId) {
        toast.error('Save the workflow before downloading');
      }
      return false;
    }
    setIsDownloadingWorkflow(true);
    try {
      const { blob, filename } = await downloadAgencyWorkflow(
        token,
        agencyId,
        workflowId,
        workflow?.name || 'workflow',
      );
      triggerBrowserDownload(blob, filename);
      toast.success('Workflow downloaded');
      return true;
    } catch (error) {
      toast.error(
        getSafeWorkflowPortableError(error, 'Unable to download this workflow.'),
      );
      return false;
    } finally {
      setIsDownloadingWorkflow(false);
    }
  }, [
    agencyId,
    isDownloadingWorkflow,
    isNewWorkflow,
    token,
    workflow?.name,
    workflowId,
  ]);

  const { openFileImport, openUrlImport } = useMemo(
    () => createWorkflowImportOpeners(setWorkflowImportMode, setWorkflowImportOpen),
    [],
  );

  const handleWorkflowImported = useMemo(
    () => createWorkflowImportedHandler({
      setWorkflowImportOpen,
      navigate,
      getBuilderPath: (workflowId) => getAgencyBuilderPath(agencyId, workflowId),
      toastSuccess: toast.success,
    }),
    [agencyId, navigate],
  );

  const handleValidateAndPublish = async () => {
    setIsValidating(true);
    setPublishError(null);
    
    try {
      // First validate
      const validateResponse = await fetch(`${API_URL}/api/workflows/${agencyId}/${workflowId}/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const validateData = await validateResponse.json();
      setValidationIssues(validateData.issues || []);
      
      if (!validateData.can_publish) {
        setPublishError({
          message: 'Cannot publish workflow due to validation issues',
          issues: validateData.blocking_issues
        });
        return;
      }
      
      // Then publish
      const publishResponse = await fetch(`${API_URL}/api/workflows/${agencyId}/${workflowId}/publish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const publishData = await publishResponse.json();
      
      if (publishData.success) {
        setWorkflow(prev => ({ ...prev, status: 'active' }));
        setHasUnsavedChanges(false);
      } else {
        setPublishError({
          message: publishData.message,
          issues: publishData.blocking_issues
        });
      }
    } catch (error) {
      console.error('Publish failed:', error);
      setPublishError({
        message: 'Failed to publish workflow. Please try again.',
        issues: []
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handlePublish = () => {
    if (isNewWorkflow) {
      // For new workflows, just update status locally (would save first in real implementation)
      setWorkflow(prev => ({ ...prev, status: 'active' }));
      setHasUnsavedChanges(false);
    } else {
      handleValidateAndPublish();
    }
  };

  const handleTogglePublish = (publish) => {
    const isPublished = workflow.status === 'active';
    if (publish === isPublished) return;

    recordWorkflowChange(publish ? 'Published workflow' : 'Set workflow to draft');

    if (publish) {
      handlePublish();
      return;
    }

    setWorkflow((prev) => ({ ...prev, status: 'draft' }));
    setHasUnsavedChanges(true);
  };

  const editorLayoutFlags = useMemo(() => ({
    variant: 'agency',
    showActionsPanel,
    showTriggerPicker,
    showEmailEditor,
    showWaitEditor,
    showSMSEditor,
    showWebhookEditor,
    showGrantPortalEditor,
    showRevokePortalEditor,
    showGrantOfferEditor,
    showRevokeOfferEditor,
    showApplyTagEditor,
    showRemoveTagEditor,
    showConditionEditor,
    showNotificationEditor,
    showPreparePasswordResetEditor,
    showRunTestPanel,
    runTestExpanded,
  }), [
    showActionsPanel,
    showTriggerPicker,
    showEmailEditor,
    showWaitEditor,
    showSMSEditor,
    showWebhookEditor,
    showGrantPortalEditor,
    showRevokePortalEditor,
    showGrantOfferEditor,
    showRevokeOfferEditor,
    showApplyTagEditor,
    showRemoveTagEditor,
    showConditionEditor,
    showNotificationEditor,
    showPreparePasswordResetEditor,
    showRunTestPanel,
    runTestExpanded,
  ]);

  const {
    builderRef,
    editorPanelWidth,
    editorOverlayMode,
    getPanelShellProps,
  } = useWorkflowEditorLayout(editorLayoutFlags);

  const panelShellOptions = useMemo(
    () => ({ onOverlayDismiss: closeEditorOverlay }),
    [closeEditorOverlay],
  );

  const navigateToSettings = (section) => {
    navigate(`/agency/${agencyId}/settings?section=${section}`);
  };

  const tabs = [
    { id: 'builder', label: 'Builder', icon: Zap },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'statistics', label: 'Statistics', icon: TrendingUp },
    { id: 'enrollment', label: 'Enrollment History', icon: History },
    { id: 'logs', label: 'Execution Logs', icon: BarChart3 },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="workflow-builder-loading">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100" data-testid="workflow-builder-page">
      <WorkflowBuilderChrome
        variant="agency"
        backLabel="Back"
        onBack={() => guardAction(() => navigate(`/agency/${agencyId}/automations`))}
        workflowName={workflow.name}
        onWorkflowNameFocus={() => {
          nameEditSnapshot.current = workflow.name;
        }}
        onWorkflowNameBlur={() => {
          if (
            nameEditSnapshot.current !== null
            && nameEditSnapshot.current !== workflow.name
          ) {
            undoRedo.recordChange('Renamed workflow', {
              ...workflow,
              name: nameEditSnapshot.current,
            });
          }
          nameEditSnapshot.current = null;
        }}
        onWorkflowNameChange={(name) => {
          setWorkflow((prev) => ({ ...prev, name }));
          setHasUnsavedChanges(true);
        }}
        workflowStatus={workflow.status}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        onSave={handleSave}
        onDownload={handleDownload}
        isDownloading={isDownloadingWorkflow}
        onImportFromFile={openFileImport}
        onImportFromUrl={openUrlImport}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          guardAction(() => {
            closeAllPanels();
            setActiveTab(tabId);
          });
        }}
        undoRedo={undoRedo}
        onOpenRunTest={() => {
          closeAllPanels();
          setShowRunTestPanel(true);
        }}
        isPublished={workflow.status === 'active'}
        onTogglePublish={handleTogglePublish}
        publishDisabled={saving || isValidating}
        publishErrorBanner={publishError ? (
          <div className="px-4 py-3 bg-red-50 border-t border-red-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="font-medium text-red-800">{publishError.message}</p>
                {publishError.issues?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {publishError.issues.map((issue, idx) => (
                      <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                        <span>• {issue.message}</span>
                        {issue.cta_path && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs text-red-700 border-red-300 hover:bg-red-100"
                            onClick={() => navigateToSettings(issue.cta_path)}
                          >
                            <ArrowUpRight size={12} className="mr-1" />
                            {issue.cta_label}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="button" onClick={() => setPublishError(null)} className="p-1 hover:bg-red-100 rounded">
                <X size={16} className="text-red-500" />
              </button>
            </div>
          </div>
        ) : null}
      />

      {/* Main Content */}
      <div ref={builderRef} className="flex min-h-0 flex-1 overflow-hidden">
        {activeTab === 'builder' ? (
          <WorkflowBuilderUtilityShell
            workflow={workflow}
            setWorkflow={setWorkflow}
            workflowId={workflowId}
            token={token}
            apiBase={`${API_URL}/api/workflows/${agencyId}`}
            builderPathPrefix={`/agency/${agencyId}/automations/builder`}
            executionPathView={executionPathView}
            onExitExecutionPath={() => setExecutionPathView(null)}
            onUnsavedChange={() => setHasUnsavedChanges(true)}
            contentClassName="p-6"
            variant="agency"
            editorPanelWidth={editorPanelWidth}
            editorOverlayMode={editorOverlayMode}
            onEditorOverlayDismiss={closeEditorOverlay}
            editorPanel={(
              <>
                {showActionsPanel && (
                  <WorkflowActionsPanel
                    isOpen={showActionsPanel}
                    {...getPanelShellProps('actions', panelShellOptions)}
                    allowAdvancedActions
                    onClose={closeAllPanels}
                    onSelectAction={handleSelectAction}
                  />
                )}

                {showTriggerPicker && (
                  <WorkflowTriggerPicker
                    isOpen={showTriggerPicker}
                    {...getPanelShellProps('trigger-picker', panelShellOptions)}
                    onClose={closeAllPanels}
                    onSelectTrigger={handleSelectTrigger}
                    activeTriggers={normalizeWorkflowTriggers(workflow)}
                    editingTriggerId={editingTriggerId}
                  />
                )}

                {showEmailEditor && (
                  <EmailActionComposer
                    isOpen={showEmailEditor}
                    {...getPanelShellProps('email', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    channelStatus={channelStatus}
                    onNavigateToSettings={navigateToSettings}
                    workflowStatus={workflow.status}
                    token={token}
                    agencyId={agencyId}
                    scope="agency"
                    workflowId={workflowId}
                  />
                )}

                {showWaitEditor && (
                  <WaitNodeEditor
                    isOpen={showWaitEditor}
                    {...getPanelShellProps('wait', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                  />
                )}

                {showSMSEditor && (
                  <SMSNodeEditor
                    isOpen={showSMSEditor}
                    {...getPanelShellProps('sms', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    onDelete={handleRemoveStep}
                    channelStatus={channelStatus}
                    onNavigateToSettings={navigateToSettings}
                    token={token}
                    workflowId={workflowId}
                  />
                )}

                {showWebhookEditor && editingStep && (
                  <WebhookNodeEditor
                    step={editingStep}
                    onClose={closeAllPanels}
                    onSave={(updatedStep) => {
                      handleSaveStep(updatedStep);
                      closeAllPanels();
                    }}
                    agencyId={agencyId}
                    getAuthHeaders={() => ({ Authorization: `Bearer ${token}` })}
                  />
                )}

                {showGrantPortalEditor && (
                  <GrantPortalAccessEditor
                    isOpen={showGrantPortalEditor}
                    {...getPanelShellProps('grant-portal', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showRevokePortalEditor && (
                  <RevokePortalAccessEditor
                    isOpen={showRevokePortalEditor}
                    {...getPanelShellProps('revoke-portal', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showGrantOfferEditor && (
                  <GrantOfferAccessEditor
                    isOpen={showGrantOfferEditor}
                    {...getPanelShellProps('grant-offer', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showRevokeOfferEditor && (
                  <RevokeOfferAccessEditor
                    isOpen={showRevokeOfferEditor}
                    {...getPanelShellProps('revoke-offer', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showApplyTagEditor && (
                  <ApplyTagEditor
                    isOpen={showApplyTagEditor}
                    {...getPanelShellProps('apply-tag', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                    agencyId={agencyId}
                  />
                )}

                {showRemoveTagEditor && (
                  <RemoveTagEditor
                    isOpen={showRemoveTagEditor}
                    {...getPanelShellProps('remove-tag', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                    agencyId={agencyId}
                  />
                )}

                {showConditionEditor && (
                  <IfElseConditionEditor
                    isOpen={showConditionEditor}
                    {...getPanelShellProps('condition', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                    agencyId={agencyId}
                    workflowSteps={workflow.steps}
                    workflowTrigger={workflow.trigger}
                  />
                )}

                {showNotificationEditor && (
                  <InternalNotificationEditor
                    isOpen={showNotificationEditor}
                    {...getPanelShellProps('notification', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showPreparePasswordResetEditor && (
                  <PreparePasswordResetEditor
                    isOpen={showPreparePasswordResetEditor}
                    {...getPanelShellProps('prepare-password-reset', panelShellOptions)}
                    onClose={closeAllPanels}
                    groupItem={editingGroup}
                    onSave={handleSaveGroup}
                    advancedMode={false}
                  />
                )}

                {showRunTestPanel && (
                  <WorkflowRunTestPanel
                    isOpen={showRunTestPanel}
                    {...getPanelShellProps('run-test', panelShellOptions)}
                    onClose={closeAllPanels}
                    onExpandedChange={setRunTestExpanded}
                    token={token}
                    workflowId={workflowId}
                    agencyId={agencyId}
                    scope="agency"
                    executionAvailable={!isNewWorkflow}
                  />
                )}
              </>
            )}
          >
            {({ isBuilderReadOnly, highlightedStepId, pathStepStates }) => (
              <WorkflowBuilderMainChain
                width={actionLaneLayout.width}
                wrapperWidth={actionLaneLayout.wrapperWidth}
                centerX={actionLaneLayout.centerX}
                globalCenterX={actionLaneLayout.globalCenterX}
              >
                <WorkflowTriggerCanvasSection
                  workflow={workflow}
                  readOnly={isBuilderReadOnly}
                  workflowId={workflowId}
                  token={token}
                  agencyId={agencyId}
                  onTriggersChange={handleTriggersChange}
                  onAddTrigger={openAddTriggerPicker}
                  onEditTrigger={openEditTriggerPicker}
                  onConnectorLayoutChange={handleTriggerConnectorLayoutChange}
                />

                <WorkflowBuilderStepList
                  steps={workflow.steps}
                  theme="light"
                  readOnly={isBuilderReadOnly}
                  columnWidth={actionLaneLayout.width}
                  connectorCenterX={actionLaneLayout.centerX}
                  columnOffset={actionLaneLayout.offset}
                  selectedStepId={editingStep?.id}
                  selectedGroupId={editingGroup?.groupId}
                  advancedMode={false}
                  onEditGroup={openEditGroup}
                  pathStepStates={pathStepStates}
                  onStepsChange={handleStepsChange}
                  onAddStep={handleAddStep}
                  onRemove={handleRemoveStep}
                  renderStep={(step, index, helpers) => (
                    <StepNode
                      step={step}
                      index={index}
                      onRemove={handleRemoveStep}
                      onEdit={openStepEditor}
                      channelStatus={channelStatus}
                      validationIssues={validationIssues}
                      isMoving={helpers.isMovingStep(step.id)}
                      isSelected={helpers.isSelected(step.id)}
                      onDragHandleStart={helpers.onDragHandleStart}
                      onContextMenuAction={helpers.onContextMenuAction}
                      hasClipboard={helpers.hasClipboard}
                      readOnly={isBuilderReadOnly}
                      highlighted={highlightedStepId === step.id}
                      pathState={pathStepStates?.[step.id]}
                    />
                  )}
                  renderConditionBranch={(step, index, _helpers, branchHelpers) => (
                    isConditionStep(step) ? (
                      <WorkflowConditionBranchCanvas
                        step={step}
                        theme="light"
                        readOnly={isBuilderReadOnly}
                        selectedBranchStepId={editingBranchContext ? editingStep?.id : null}
                        onGraphHeightChange={branchHelpers?.onGraphHeightChange}
                        onAddStepToBranch={handleAddBranchStep}
                        renderBranchStep={(branchStep, branchContext) => (
                          <StepNode
                            step={branchStep}
                            onRemove={handleRemoveStep}
                            onEdit={openStepEditor}
                            isMoving={false}
                            isSelected={branchContext.isSelected}
                            onDragHandleStart={() => {}}
                            onContextMenuAction={(action, stepId) => (
                              handleBranchStepContextAction(action, stepId, branchContext)
                            )}
                            hasClipboard={false}
                            readOnly={isBuilderReadOnly}
                            columnWidth={BRANCH_CARD_MIN_WIDTH}
                            isBranchStep
                          />
                        )}
                      />
                    ) : null
                  )}
                />

                {shouldRenderMainChainEndNode(workflow.steps) ? (
                <div
                  className="relative"
                  style={{
                    width: actionLaneLayout.width,
                    marginLeft: actionLaneLayout.offset,
                    height: 48,
                  }}
                  data-workflow-canvas-node
                  data-workflow-action-lane-end
                  data-workflow-main-center-x={actionLaneLayout.centerX}
                  data-workflow-end-center-x={actionLaneLayout.globalCenterX}
                  data-workflow-main-global-center-x={actionLaneLayout.globalCenterX}
                >
                  <div
                    className="absolute px-6 py-3 bg-slate-100 rounded-full text-sm font-medium text-slate-500"
                    style={{ left: actionLaneLayout.centerX, transform: 'translateX(-50%)' }}
                  >
                    End of Workflow
                  </div>
                </div>
                ) : null}
              </WorkflowBuilderMainChain>
            )}
          </WorkflowBuilderUtilityShell>
        ) : (
        <div
          className="flex-1 overflow-y-auto bg-slate-50 p-6"
        >
          {activeTab === 'settings' && (
            <WorkflowSettingsTab
              workflow={workflow}
              settings={normalizeWorkflowSettings(workflow.settings)}
              onSettingsChange={handleSettingsChange}
              onMetaChange={(patch) => {
                setWorkflow((prev) => ({ ...prev, ...patch }));
                setHasUnsavedChanges(true);
              }}
              token={token}
              showPlatformFields={false}
            />
          )}

          {activeTab === 'statistics' && !isNewWorkflow && (
            <WorkflowStatisticsPanel workflow={workflow} token={token} />
          )}

          {activeTab === 'statistics' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <TrendingUp className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No statistics yet</p>
              <p className="mt-1 text-sm text-slate-500">Save this workflow to start collecting email performance statistics.</p>
            </div>
          )}

          {activeTab === 'enrollment' && !isNewWorkflow && (
            <WorkflowEnrollmentHistoryTab
              token={token}
              workflowId={workflowId}
              agencyId={agencyId}
              onViewExecutionPath={handleViewExecutionPath}
            />
          )}

          {activeTab === 'enrollment' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <Users className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No enrollments yet</p>
              <p className="mt-1 text-sm text-slate-500">Save this workflow to start tracking enrollment history.</p>
            </div>
          )}

          {activeTab === 'logs' && !isNewWorkflow && (
            <WorkflowExecutionLogsTab
              token={token}
              workflowId={workflowId}
              agencyId={agencyId}
              onViewExecutionPath={handleViewExecutionPath}
            />
          )}

          {activeTab === 'logs' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <BarChart3 className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No execution logs yet</p>
              <p className="mt-1 text-sm text-slate-500">Logs will appear once this workflow starts running.</p>
            </div>
          )}
        </div>
        )}

      </div>

      <WorkflowImportDialog
        open={workflowImportOpen}
        onOpenChange={setWorkflowImportOpen}
        mode={workflowImportMode}
        scope="agency"
        agencyId={agencyId}
        token={token}
        onImported={handleWorkflowImported}
      />
    </div>
  );
};

const WorkflowBuilderPage = () => (
  <ActionEditorUnsavedProvider>
    <WorkflowBuilderPageInner />
  </ActionEditorUnsavedProvider>
);

export default WorkflowBuilderPage;
